import { useState, useMemo } from 'react';
import { useApi } from '../hooks/useApi.js';
import { fetchAchievements } from '../lib/api.js';
import { Seo } from '../components/Seo.jsx';
import { PageHeader } from '../components/PageHeader.jsx';
import { FilterBar } from '../components/FilterBar.jsx';
import { usePage } from '../hooks/usePage.js';

const CATEGORIES = [
  { id: 'academic', label: 'Academic' },
  { id: 'sport',    label: 'Sport' },
  { id: 'art',      label: 'Art & culture' },
  { id: 'science',  label: 'Science & tech' },
];

const formatMonth = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }); }
  catch { return ''; }
};

const PLACEHOLDER = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 9"><rect width="16" height="9" fill="%231d4d3a"/></svg>';

export function AchievementsPage() {
  const [category, setCategory] = useState('all');
  const head = usePage('achievements');
  const { data, loading } = useApi(fetchAchievements, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    if (category === 'all') return data;
    return data.filter((a) => a.category === category);
  }, [data, category]);

  return (
    <>
      <Seo title="Achievements" description={head.metaDescription} />

      <PageHeader
        crumbs={[{ label: 'Home', to: '/' }, { label: 'Media & News' }, { label: 'Achievements' }]}
        headline={head.headline}
        lede={head.lede}
        image={head.banner}
      />

      <section>
        <div className="container">
          <FilterBar options={CATEGORIES} value={category} onChange={setCategory} />

          {loading && <p style={{ marginTop: 32, color: 'var(--ink-3)' }}>Loading achievements…</p>}

          {!loading && filtered.length > 0 && (
            <div
              className="news-grid"
              style={{ marginTop: 32, gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))' }}
            >
              {filtered.map((a) => (
                <article className="news-card" key={a.id} data-cat={a.category}>
                  <div className="photo" style={{ backgroundImage: `url('${a.image || PLACEHOLDER}')` }} />
                  <div className="body">
                    <div className="meta">
                      <span className="tag">{CATEGORIES.find((c) => c.id === a.category)?.label || a.category}</span>
                      <span className="dot" />
                      {a.achievedOn && <span>{formatMonth(a.achievedOn)}</span>}
                    </div>
                    <h3>{a.title}</h3>
                    {(a.recipient || a.description) && (
                      <p>{a.recipient}{a.recipient && a.description ? ' — ' : ''}{a.description}</p>
                    )}
                  </div>
                </article>
              ))}
            </div>
          )}

          {!loading && filtered.length === 0 && (
            <p style={{ color: 'var(--ink-3)', textAlign: 'center', marginTop: 48 }}>
              {category === 'all' ? 'No achievements published yet.' : 'No achievements in this category yet.'}
            </p>
          )}
        </div>
      </section>
    </>
  );
}
